// EXEMPT
import { IS_DEV } from "@/__main__/constants.mjs";
import type {
  AnyGameDefinition,
  GameDefinition,
} from "@/app/game-definition.mjs";
import { Game2xko, GameRust } from "@/app/upcoming-game-definitions.mjs";
import GameApex from "@/game-apex/definition.mjs";
import GameCS2 from "@/game-csgo/definition.mjs";
import GameDeadlock from "@/game-deadlock/definition.mjs";
import GameEFT from "@/game-eft/definition.mjs";
import GameFortnite from "@/game-fortnite/definition.mjs";
import GameLoL from "@/game-lol/definition.mjs";
import GameMarvelRivals from "@/game-marvel-rivals/definition.mjs";
import GameMinecraft from "@/game-minecraft/definition.mjs";
import GameOW2 from "@/game-ow2/definition.mjs";
import GameRocketLeague from "@/game-rocket/definition.mjs";
import GameTFT from "@/game-tft/definition.mjs";
import GameUnknown from "@/game-unknown/definition.mjs";
import GameValorant from "@/game-val/definition.mjs";

const gamesList = [
  GameLoL,
  GameTFT,
  GameValorant,
  GameFortnite,
  GameMarvelRivals,
  GameCS2,
  GameUnknown,
  GameApex,
  GameEFT,
  GameDeadlock,
  Game2xko,
  GameRust,
  GameOW2,
  GameRocketLeague,
  GameMinecraft,
] as const satisfies ReadonlyArray<AnyGameDefinition>;

export type GameSymbol = (typeof gamesList)[number]["gameSymbol"];

if (IS_DEV) {
  (async () => {
    const { default: checkGameDefinition } = await import(
      "@/app/check-game-definition.mjs"
    );
    gamesList.forEach(checkGameDefinition);
  })();
}

export default gamesList;

/**
 * Picks the game definition from `gamesList` that matches the given `GameSymbol`.
 *
 * <b>Example:</b>
 * ```
 * const gamesList = [
 *  { gameSymbol: APEX, installLink: "someurl" as const },
 *  { gameSymbol: LOL, installLink: "someotherurl" },
 *  { gameSymbol: VAL }
 * ];
 *
 * type ApexGame = PickGame<typeof APEX>;
 * ```
 *
 * `ApexGame`'s resulting type would be:
 * ```
 * { gameSymbol: APEX, installLink: "someurl" as const }
 * ```
 */
export type PickGame<S extends symbol> =
  (typeof gamesList)[number] extends infer Game
    ? Game extends GameDefinition<S>
      ? Game
      : never
    : never;

export function getGameDefinition<Game extends GameSymbol>(
  gameSymbol: Game,
): AnyGameDefinition {
  return gamesList.find(
    (game): game is PickGame<Game> => game.gameSymbol === gameSymbol,
  );
}

/**
 * Creates a union of GameSymbols, but only includes GameDefinitions that have the given
 * property `K` defined.
 *
 * <b>Example:</b>
 * ```
 * const gamesList = [
 *  { gameSymbol: APEX, installLink: "someurl" as const },
 *  { gameSymbol: LOL, installLink: "someotherurl" },
 *  { gameSymbol: VAL }
 * ];
 *
 * type InstallableGames = GamesWithProp<"installLink">;
 * ```
 *
 * * APEX will be included because `installLink` is defined.
 * * LOL will be included because `installLink` is defined.
 * * VAL will be excluded because `installLink` is undefined.
 *
 * Therefore `InstallableGames`'s resulting type would be:
 * ```
 * { gameSymbol: APEX, installLink: "someurl" } | { gameSymbol: LOL, installLink: string }
 * ```
 */
export type GamesWithProp<K> = (typeof gamesList)[number] extends infer Game
  ? Game extends object
    ? K extends keyof Game
      ? Game
      : never
    : never
  : never;

/**
 * Returns all `GameDefinitions`s that have the given property `K` defined.
 */
export function getGamesWithProp<K extends keyof AnyGameDefinition>(
  prop: K,
): GamesWithProp<K>[] {
  return gamesList.filter((game): game is GamesWithProp<K> => prop in game);
}

/**
 * Creates a union of GameDefinitions, but only includes those where the given property `K`
 * extends the given type `V`.
 *
 * <b>Example:</b>
 * ```
 * const gamesList = [
 *  { gameSymbol: APEX, isActive: true as const },
 *  { gameSymbol: LOL, isActive: false as const },
 *  { gameSymbol: VAL, isActive: IS_DEV }
 * ];
 *
 * type ActiveGames = GamesWhereProp<"isActive", true>;
 * ```
 *
 * * APEX will be included because `isActive` is `true`.
 * * LOL will be excluded because `isActive` is `false`.
 * * VAL will be included because `IS_DEV` is conditionally `true` or `false` based
 *   on runtime information. This may also occur if `isActive` is not `const`.
 *
 * Therefore `ActiveGames`'s resulting type would be:
 * ```
 * { gameSymbol: APEX, isActive: true } | { gameSymbol: VAL, installLink: string }
 * ```
 */
export type GamesWhereProp<
  K extends keyof AnyGameDefinition,
  V extends AnyGameDefinition[K],
> = (typeof gamesList)[number] extends infer Game extends AnyGameDefinition
  ? K extends keyof Game
    ? V extends Game[K]
      ? Game
      : never
    : never
  : never;

/**
 * Creates an object where K is all `GameSymbol`s where `GameDefinition[K]` is defined.
 *
 * <b>Example:</b>
 * ```
 * const gamesList = [
 *  { gameSymbol: APEX, installLink: "someurl" as const },
 *  { gameSymbol: LOL, installLink: "someotherurl" },
 *  { gameSymbol: VAL }
 * ];
 *
 * type GameInstallLinks = GamesMapPickProp<"installLink">;
 * ```
 *
 * * APEX will be included because `installLink` is defined, and its value
 *   will be a string literal since it is `const`.
 * * LOL will be included because `installLink` is defined. Its value will
 *   be a `string` since it is not `const`.
 * * VAL will be excluded because `installLink` is undefined.
 *
 * Therefore `GameInstallLinks`'s resulting type would be:
 * ```
 * {
 *   [APEX]: "someurl",
 *   [LOL]: string
 * }
 * ```
 * */
export type GamesPropMap<K extends keyof AnyGameDefinition> =
  GamesWithProp<K> extends infer MatchedGame extends AnyGameDefinition
    ? {
        [S in MatchedGame["gameSymbol"]]: PickGame<S>[K];
      }
    : never;

type YankGameProp<K> =
  GamesWithProp<K> extends infer MatchedGame
    ? K extends keyof MatchedGame
      ? MatchedGame[K]
      : never
    : never;

export function deriveGamesPropMap<K extends keyof AnyGameDefinition>(
  prop: K,
): GamesPropMap<K>;

export function deriveGamesPropMap<
  K extends keyof AnyGameDefinition,
  R = YankGameProp<K>,
>(
  prop: K,
  mapper?: (acc: YankGameProp<K>) => R,
): {
  [S in GamesWithProp<K>["gameSymbol"]]: R;
};

/**
 * Pulls the given key `K` out of each game definition that it's defined in and returns
 * an object of `{ GameSymbol: GameDefinition[K] }`.
 *
 * You may provide a `mapper` function to modify the value of each `GameDefinition[K]`,
 * usually to access a nested property.
 */
export function deriveGamesPropMap<
  K extends keyof AnyGameDefinition,
  R = YankGameProp<K>,
>(
  prop: K,
  mapper?: (acc: YankGameProp<K>) => R,
): {
  [S in GamesWithProp<K>["gameSymbol"]]: R;
} {
  return gamesList.reduce(
    (obj, game) => {
      if (prop in game) {
        // @ts-expect-error - This is fine, since we know the mapping is correct
        obj[game.gameSymbol] = mapper ? mapper(game[prop]) : game[prop];
      }

      return obj;
    },
    {} as {
      [S in GamesWithProp<K>["gameSymbol"]]: R;
    },
  );
}
